import {combine, createEvent, merge, sample} from 'effector';
import {createGate} from 'effector-react';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {InnerFiltersElement, InnerSortingsElement} from '@shared/types/grid';
import {SelectOption} from '@shared/ui/Select';

import * as api from './api';
import {
    DictionarySearchResponse,
    JobDictionaryItem,
    JobTemplateDto,
    JobTemplateFilterNode,
    JobTemplateSearchResponse,
} from './types';

const domain = rootDomain.createDomain('JobTemplates');

export const JobTemplatesGate = createGate('JobTemplates');
export const JobTemplateDetailsGate = createGate<{jobTemplateId: string}>('JobTemplateDetails');

const fetchJobTemplatesFx = domain.createEffect({handler: api.fetchJobTemplates});
const fetchJobTemplateFx = domain.createEffect({handler: api.fetchJobTemplate});
const saveJobTemplateFx = domain.createEffect({handler: api.updateJobTemplate});
const fetchActiveStatusesFx = domain.createEffect({handler: api.fetchActiveStatuses});

[fetchJobTemplatesFx, fetchJobTemplateFx, saveJobTemplateFx, fetchActiveStatusesFx].forEach((fx) =>
    fx.failData.watch(showRestError),
);

const data$ = domain
    .createStore<JobTemplateSearchResponse | null>(null)
    .on(fetchJobTemplatesFx.doneData, (_, data) => data)
    .reset(JobTemplatesGate.close);

const template$ = domain
    .createStore<JobTemplateDto | null>(null)
    .on(fetchJobTemplateFx.doneData, (_, data) => data)
    .on(saveJobTemplateFx.doneData, (_, data) => data)
    .reset(JobTemplateDetailsGate.close);

const activeStatuses$ = domain
    .createStore<JobDictionaryItem[]>([])
    .on(fetchActiveStatusesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(JobTemplatesGate.close, JobTemplateDetailsGate.close);

const search$ = domain.createStore('').reset(JobTemplatesGate.close);
const statusCode$ = domain.createStore<string | null>(null).reset(JobTemplatesGate.close);
const sortings$ = domain
    .createStore<Record<string, boolean | undefined>>({})
    .reset(JobTemplatesGate.close);
const pageNumber$ = domain.createStore(0).reset(JobTemplatesGate.close);
const pageSize$ = domain.createStore(10).reset(JobTemplatesGate.close);

export const searchChanged = createEvent<string>();
export const statusCodeChanged = createEvent<string | null>();
export const sortingsChanged = createEvent<InnerSortingsElement[]>();
export const pageNumberChanged = createEvent<number>();
export const pageSizeChanged = createEvent<number>();

search$.on(searchChanged, (_, value) => value);
statusCode$.on(statusCodeChanged, (_, value) => value);
sortings$.on(sortingsChanged, (state, sortings) => ({
    ...state,
    ...Object.fromEntries(sortings.map(({id, value}) => [id, value])),
}));
pageNumber$.on(pageNumberChanged, (_, page) => page);
pageSize$.on(pageSizeChanged, (_, size) => size);
pageNumber$.reset(searchChanged, statusCodeChanged, pageSizeChanged);

const buildFilter = ({
    search,
    statusCode,
}: {
    search: string;
    statusCode: string | null;
}): JobTemplateFilterNode => {
    const children: JobTemplateFilterNode[] = [{type: 'EQ', field: 'type.code', values: ['FLOW']}];

    const trimmedSearch = search.trim();
    if (trimmedSearch) {
        children.push({type: 'ILIKE', field: 'title', values: [`%${trimmedSearch}%`]});
    }

    if (statusCode) {
        children.push({type: 'EQ', field: 'status.code', values: [statusCode]});
    }

    return children.length === 1 ? children[0] : {type: 'AND', children};
};

const mapSorts = (sortings: Record<string, boolean | undefined>) => {
    const [field, value] = Object.entries(sortings).find(([, sort]) => sort != null) ?? [];
    if (!field || value == null) return null;
    return [{field, order: value ? 'DESC' : 'ASC'} as const];
};

export const filtersChanged = createEvent<InnerFiltersElement[]>();
filtersChanged.watch((filters) => {
    const {id, value} = filters[0] ?? {};
    if (id === 'status') {
        statusCodeChanged(((value as SelectOption<string> | null)?.value ?? null) as string | null);
    }
});

export const filtersReset = createEvent<string[]>();
filtersReset.watch((ids) => {
    if (ids[0] === 'status') statusCodeChanged(null);
});

sample({
    clock: JobTemplatesGate.open,
    target: fetchActiveStatusesFx,
});

sample({
    clock: JobTemplateDetailsGate.open,
    fn: ({jobTemplateId}) => jobTemplateId,
    target: fetchJobTemplateFx,
});

sample({
    clock: JobTemplateDetailsGate.open,
    target: fetchActiveStatusesFx,
});

sample({
    clock: merge([
        JobTemplatesGate.open,
        searchChanged,
        statusCodeChanged,
        sortingsChanged,
        pageNumberChanged,
        pageSizeChanged,
    ]),
    source: combine({
        search: search$,
        statusCode: statusCode$,
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
    }),
    filter: JobTemplatesGate.status,
    fn: ({search, statusCode, sortings, pageNumber, pageSize}) => ({
        filter: buildFilter({search, statusCode}),
        sorts: mapSorts(sortings),
        page: {page: pageNumber, size: pageSize},
    }),
    target: fetchJobTemplatesFx,
});

const toOption = (item: JobDictionaryItem): SelectOption<string> => ({
    value: item.code,
    label: item.title?.en ?? Object.values(item.title ?? {})[0] ?? item.code,
});

const filters$ = combine(
    {
        statusCode: statusCode$,
        statuses: activeStatuses$,
    },
    ({statusCode, statuses}) => ({
        status: statusCode
            ? toOption(
                  statuses.find((item) => item.code === statusCode) ??
                      ({code: statusCode} as JobDictionaryItem),
              )
            : undefined,
    }),
);

const pending$ = combine([fetchJobTemplatesFx.pending, fetchActiveStatusesFx.pending], (list) =>
    list.some(Boolean),
);

const detailsPending$ = combine(
    [fetchJobTemplateFx.pending, saveJobTemplateFx.pending, fetchActiveStatusesFx.pending],
    (list) => list.some(Boolean),
);

const $jobTemplatesState = combine({
    data: data$,
    activeStatuses: activeStatuses$,
    search: search$,
    filters: filters$,
    sortings: sortings$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    pending: pending$,
});

const $jobTemplateDetailsState = combine({
    template: template$,
    activeStatuses: activeStatuses$,
    pending: detailsPending$,
});

export const jobTemplatesModel = {
    state: $jobTemplatesState,
    searchChanged,
    filtersChanged,
    filtersReset,
    sortingsChanged,
    pageNumberChanged,
    pageSizeChanged,
};

export const jobTemplateDetailsModel = {
    state: $jobTemplateDetailsState,
    saveJobTemplate: saveJobTemplateFx,
};
